import { Event as SentryEvent, EventHint, Breadcrumb } from "@sentry/browser";
import { ILoggerObjectTree } from "../types";
import { envConfig } from "@env/env-config";
import { SentryService } from "./sentry.service";
import { IDnsConfig, ISentryConfig, ISentryUser } from "./types";



/**
 * @desc sentry信息发送前的信息加工与过滤函数，若返回null则信息不会发送
 * @param {*} event 发送给sentry后台的数据对象
 * @param {*} hint 包含原始错误信息的对象
 */
function beforeSendFn(event: SentryEvent, hint?: EventHint): PromiseLike<SentryEvent | null> | SentryEvent | null {
    // 目前暂未添加对于普通错误的输出加工处理
    if (!event.extra || !event.extra.errorType) {
        return event;
    }

    // 以下逻辑均为网络请求错误时，对于错误数据的加工处理
    // 加工sentry错误的副标题
    if (event.exception && event.exception.values && hint) {
        event.exception.values.forEach(function (item) {
            var originalException = hint.originalException;

            if (originalException) {
                if (originalException instanceof Error) {
                    if (event.extra && event.extra.errorType === envConfig.sentry.errorType.RESOLVE) {
                        item.value = JSON.stringify(originalException);
                    }
                } else if (typeof (originalException) == "string") {
                    item.value = originalException;
                }
            }
        });

    }

    // 添加接口请求相关数据
    if (event.extra) {
        let req = {} as any;
        if (event.request && event.request.url) {
            req.requestPage = event.request.url.split("?")[0];
        }

        let crumbs = event.breadcrumbs;
        if (crumbs != undefined && crumbs.length > 0) {
            let curCrumb = crumbs[crumbs.length - 1];
            if (curCrumb && curCrumb.data) {
                req.method = curCrumb.data.method;
                req.requestUrl = curCrumb.data.url;
            }
        }
        event.extra.request = req;

        // 添加接口响应相关数据
        if (hint) {
            event.extra.response = hint.originalException;
        }
    }

    // 由于我们已经自定义了请求相关数据，这里删除sentry自带序列化请求数据
    delete event.extra.__serialized__;
    // 删除为区分错误添加的错误类型
    delete event.extra.errorType;

    return event;
    // tip:如果return null，sentry将忽略掉此时错误抓取
    // return null;
}

/**
 * @desc 面包屑信息加工函数
 * @param {*} breadcrumb 面包屑信息
 */
function beforeBreadcrumbFn(breadcrumb: Breadcrumb): Breadcrumb {
    // 与错误信息相同，如果返回null将不抓取此信息
    return breadcrumb;
}

export class SentryServiceProvider implements angular.IServiceProvider {

    constructor() {
    }

    private scope_configs: any;

    /**
     * 配置Rest请求
     * 
     * @param serviceName angular中的service名称
     * @param config rest配置
     * @param configs 配置对象，key需要是angular中的service名称
     */
    public config(options?: ISentryConfig) {
        let myOptions = options ?? envConfig.sentry;

        if (myOptions.dsnConfigs == undefined) {
            throw new Error("Missing dsnConfigs for sentry.");
        }

        //解决跨域不能发送请示的问题
        let dsn = this.getDsn(myOptions.dsnConfigs!);

        this.scope_configs = myOptions.scopeConfigs;

        Sentry.init({
            dsn: dsn,
            integrations: [
                // 禁用sentry面包屑获取console相关信息
                new Sentry.Integrations.Breadcrumbs({
                    console: false
                })
            ],
            // 包含如下字段的错误信息都会过滤不被抓取
            ignoreErrors: myOptions.ignoreErrors,
            // 包含如下地址的所有错误都不会抓取
            blacklistUrls: myOptions.blacklistUrls,
            // 禁用sentry默认集成的相关配置
            // defaultIntegrations: false,
            // 信息发送前的加工函数
            beforeSend: beforeSendFn,
            // 面包屑信息发送前的加工函数
            beforeBreadcrumb: beforeBreadcrumbFn
        })
    }

    public getDsn(options: IDnsConfig): string {
        if (window && window.location && window.location.protocol && window.location.host) {
            let protocol = angular.copy(window.location.protocol);
            let host = angular.copy(window.location.host);
            let path = window?.location?.pathname || "";
            if (/^\/stage/gi.test(path)) {
                return protocol + '//' + options.secret_stage + '@' + host + (host.lastIndexOf("/") == host.length - 1 ? "" : "/") + "stage/" + options.appName + '/' + options.id_stage;
            } else if (/^stage/gi.test(host)) {
                return protocol + '//' + options.secret_stage + '@' + host + (host.lastIndexOf("/") == host.length - 1 ? "" : "/") + options.appName + '/' + options.id_stage;
            } else {
                return protocol + '//' + options.secret + '@' + host + (host.lastIndexOf("/") == host.length - 1 ? "" : "/") + options.appName + '/' + options.id;
            }
        } else {
            throw new Error("B201S0182700494");
        }
    }

    /**
     * service的工厂
     */
    public $get = ["$objectTree",
        function (
            $objectTree: ILoggerObjectTree
        ) {
            return new SentryService($objectTree, this.scope_configs);
        }
    ];
}